import { MemoizedSelector, createSelector } from '@ngrx/store';
import { StateUtils, StateWithProcess } from '@spartacus/core';
import { EntriesState } from '../../../../../core/features/store/base-feature-state';
import {
  Address,
  B2BComment,
  CartSimulation,
  CheckoutKey,
  DeliveryDateInfo,
  LoaderError,
  Order,
  OrderProcessStatus,
  PaymentOption,
  PaymentSetup,
  Reinvoicing,
  ServiceOption,
  ServiceOptionType,
  ShippingOption,
} from '../../../../../core/model';
import { ProcessSelectors } from '../../../../../core/process/store';
import { ProcessIdBuilder } from '../../../../../core/user/store/util/process-id-builder';
import {
  CheckoutDeliveryStepState,
  CheckoutPaymentStepState,
  CheckoutReviewStepState,
  CheckoutShippingStepState,
  CheckoutState,
  CheckoutStepsState,
  GET_ORDER_PROCESS_STATUS_PROCESS,
  LOAD_PAYMENT_OPTIONS_PROCESS,
  LastFullPalletUpgradeEntry,
  StateWithCheckout,
} from '../checkout-state';
import { getCheckoutState } from './feature.selector';

interface PaymentOptionsState {
  paymentOptions: PaymentOption[];
  key?: CheckoutKey;
}

export const getCheckoutStepsEntries: MemoizedSelector<StateWithCheckout, EntriesState<CheckoutStepsState>> = createSelector(
  getCheckoutState,
  (state: CheckoutState) => state && state.steps
);

export const getCheckoutStepsFactory = (
  key: CheckoutKey
): MemoizedSelector<StateWithCheckout, CheckoutStepsState | undefined> => {
  return createSelector(getCheckoutStepsEntries, (state: EntriesState<CheckoutStepsState>) => (state && state[key]) || undefined);
};

export const getCartIdFactory = (key: CheckoutKey): MemoizedSelector<StateWithCheckout, string | undefined> => {
  return createSelector(getCheckoutStepsFactory(key), (state: CheckoutStepsState) => state?.cartId);
};

export const getCheckoutDeliveryStepFactory = (
  key: CheckoutKey
): MemoizedSelector<StateWithCheckout, CheckoutDeliveryStepState | undefined> => {
  return createSelector(getCheckoutStepsFactory(key), (state: CheckoutStepsState) => (state && state.delivery) || undefined);
};

export const getSelectedDeliveryAddressFactory = (key: CheckoutKey): MemoizedSelector<StateWithCheckout, Address | undefined> => {
  return createSelector(
    getCheckoutDeliveryStepFactory(key),
    (state: CheckoutDeliveryStepState) => (state && state.selectedAddress) || undefined
  );
};

export const getLastFullPalletUpgrade = (
  key: CheckoutKey
): MemoizedSelector<StateWithCheckout, LastFullPalletUpgradeEntry | undefined> => {
  return createSelector(
    getCheckoutStepsFactory(key),
    (state: CheckoutStepsState) => (state && state.lastFullPalletUpgradeEntry) || undefined
  );
};

export const getDeliveryDateInfoFactory = (
  key: CheckoutKey
): MemoizedSelector<StateWithCheckout, DeliveryDateInfo | undefined> => {
  return createSelector(
    getCheckoutDeliveryStepFactory(key),
    (state: CheckoutDeliveryStepState) => (state && state.deliveryDateInfo) || undefined
  );
};

export const getCheckoutPaymentStepFactory = (
  key: CheckoutKey
): MemoizedSelector<StateWithCheckout, CheckoutPaymentStepState | undefined> => {
  return createSelector(getCheckoutStepsFactory(key), (state: CheckoutStepsState) => (state && state.payment) || undefined);
};

export const getPaymentOptionsState = (
  key: CheckoutKey
): MemoizedSelector<StateWithProcess<PaymentOptionsState>, StateUtils.LoaderState<PaymentOptionsState>> => {
  return ProcessSelectors.getProcessStateFactory(ProcessIdBuilder.generateId(LOAD_PAYMENT_OPTIONS_PROCESS, key));
};

export const getPaymentOptions = (key: CheckoutKey): MemoizedSelector<StateWithCheckout, PaymentOption[]> => {
  return createSelector(getCheckoutPaymentStepFactory(key), (state: CheckoutPaymentStepState) => state?.paymentOptions);
};

export const getPaymentOptionsLoading = (key: CheckoutKey): MemoizedSelector<StateWithProcess<PaymentOptionsState>, boolean> => {
  return ProcessSelectors.getProcessLoadingFactory(ProcessIdBuilder.generateId(LOAD_PAYMENT_OPTIONS_PROCESS, key));
};

export const getPaymentOptionsError = (
  key: CheckoutKey
): MemoizedSelector<StateWithProcess<PaymentOptionsState>, LoaderError | undefined> => {
  return ProcessSelectors.getProcessErrorFactory(ProcessIdBuilder.generateId(LOAD_PAYMENT_OPTIONS_PROCESS, key));
};

export const getSelectedPaymentOptionFactory = (
  key: CheckoutKey
): MemoizedSelector<StateWithCheckout, PaymentOption | undefined> => {
  return createSelector(
    getCheckoutPaymentStepFactory(key),
    (state: CheckoutPaymentStepState) => (state && state.selectedPaymentOption) || undefined
  );
};

export const getPaymentSetupFactory = (key: CheckoutKey): MemoizedSelector<StateWithCheckout, PaymentSetup | undefined> => {
  return createSelector(
    getCheckoutPaymentStepFactory(key),
    (state: CheckoutPaymentStepState) => (state && state.paymentSetup) || undefined
  );
};

export const getCheckoutShippingStepFactory = (
  key: CheckoutKey
): MemoizedSelector<StateWithCheckout, CheckoutShippingStepState | undefined> => {
  return createSelector(getCheckoutStepsFactory(key), (state: CheckoutStepsState) => (state && state.shipping) || undefined);
};

export const getSelectedShippingOptionFactory = (
  key: CheckoutKey
): MemoizedSelector<StateWithCheckout, ShippingOption | undefined> => {
  return createSelector(
    getCheckoutShippingStepFactory(key),
    (state: CheckoutShippingStepState) => (state && state.selectedShippingOption) || undefined
  );
};

export const getShippingOptionConfirmedFactory = (key: CheckoutKey): MemoizedSelector<StateWithCheckout, boolean> => {
  return createSelector(
    getCheckoutShippingStepFactory(key),
    (state: CheckoutShippingStepState) => (state && state.isShippingOptionConfirmed) || false
  );
};

export const getServiceOptionsFactory = (
  key: CheckoutKey
): MemoizedSelector<StateWithCheckout, { [type: string]: ServiceOption } | undefined> => {
  return createSelector(
    getCheckoutShippingStepFactory(key),
    (state: CheckoutShippingStepState) => (state && state.serviceOptions) || undefined
  );
};

export const getServiceOptionFactory = (
  key: CheckoutKey,
  type: ServiceOptionType
): MemoizedSelector<StateWithCheckout, ServiceOption | undefined> => {
  return createSelector(
    getCheckoutShippingStepFactory(key),
    (state: CheckoutShippingStepState) => (state && state.serviceOptions[type]) || undefined
  );
};

export const getOrderFactory = (key: CheckoutKey): MemoizedSelector<StateWithCheckout, Order | undefined> => {
  return createSelector(getCheckoutStepsFactory(key), (state: CheckoutStepsState) => (state && state.order) || undefined);
};

export const getCartSimulationFactory = (key: CheckoutKey): MemoizedSelector<StateWithCheckout, CartSimulation | undefined> => {
  return createSelector(
    getCheckoutStepsFactory(key),
    (state: CheckoutStepsState) => (state && state.cartSimulation) || undefined
  );
};

export const getOrderProcessStatusFactory = (
  key: CheckoutKey
): MemoizedSelector<StateWithCheckout, OrderProcessStatus | undefined> => {
  return createSelector(
    getCheckoutStepsFactory(key),
    (state: CheckoutStepsState) => (state && state.orderProcessStatus) || undefined
  );
};

export const getOrderProcessStatusErrorFactory = (
  key: CheckoutKey
): MemoizedSelector<StateWithProcess<any>, LoaderError | undefined> =>
  ProcessSelectors.getProcessErrorFactory(ProcessIdBuilder.generateId(GET_ORDER_PROCESS_STATUS_PROCESS, key));

export const getOrderProcessStatusPollingFactory = (key: CheckoutKey): MemoizedSelector<StateWithCheckout, boolean> => {
  return createSelector(getCheckoutStepsFactory(key), (state: CheckoutStepsState) => state?.orderProcessPolling || false);
};

export const getEarliestDeliveryDateFactory = (key: CheckoutKey): MemoizedSelector<StateWithCheckout, string | undefined> => {
  return createSelector(
    getCheckoutStepsFactory(key),
    (state: CheckoutStepsState) => (state && state.earliestDeliveryDate) || undefined
  );
};

export const getOrderReviewedFactory = (key: CheckoutKey): MemoizedSelector<StateWithCheckout, boolean | undefined> => {
  return createSelector(getCheckoutStepsFactory(key), (state: CheckoutStepsState) => state?.isOrderReviewed);
};

export const getCheckoutReviewStepFactory = (
  key: CheckoutKey
): MemoizedSelector<StateWithCheckout, CheckoutReviewStepState | undefined> => {
  return createSelector(getCheckoutStepsFactory(key), (state: CheckoutStepsState) => (state && state.review) || undefined);
};

export const getOrderNoteFactory = (key: CheckoutKey): MemoizedSelector<StateWithCheckout, string | undefined> => {
  return createSelector(
    getCheckoutReviewStepFactory(key),
    (state: CheckoutReviewStepState) => (state && state.orderNote) || undefined
  );
};

export const getDeliveryNoteFactory = (key: CheckoutKey): MemoizedSelector<StateWithCheckout, string | undefined> => {
  return createSelector(
    getCheckoutReviewStepFactory(key),
    (state: CheckoutReviewStepState) => (state && state.deliveryNote) || undefined
  );
};

export const getMessageFactory = (key: CheckoutKey): MemoizedSelector<StateWithCheckout, string | undefined> => {
  return createSelector(
    getCheckoutReviewStepFactory(key),
    (state: CheckoutReviewStepState) => (state && state.message) || undefined
  );
};

export const getGoodsMarkingFactory = (key: CheckoutKey): MemoizedSelector<StateWithCheckout, string | undefined> => {
  return createSelector(
    getCheckoutReviewStepFactory(key),
    (state: CheckoutReviewStepState) => (state && state.goodsMarking) || undefined
  );
};

export const getCustomerReferenceFactory = (key: CheckoutKey): MemoizedSelector<StateWithCheckout, string | undefined> => {
  return createSelector(
    getCheckoutReviewStepFactory(key),
    (state: CheckoutReviewStepState) => (state && state.customerReference) || undefined
  );
};

export const getPhoneNumberFactory = (key: CheckoutKey): MemoizedSelector<StateWithCheckout, string | undefined> => {
  return createSelector(
    getCheckoutReviewStepFactory(key),
    (state: CheckoutReviewStepState) => (state && state.phoneNumber) || undefined
  );
};

export const getReinvoicingFactory = (key: CheckoutKey): MemoizedSelector<StateWithCheckout, Reinvoicing | undefined> => {
  return createSelector(
    getCheckoutReviewStepFactory(key),
    (state: CheckoutReviewStepState) => (state && state.reinvoicing) || undefined
  );
};

export const getInvoiceNoteFactory = (key: CheckoutKey): MemoizedSelector<StateWithCheckout, string | undefined> => {
  return createSelector(
    getCheckoutReviewStepFactory(key),
    (state: CheckoutReviewStepState) => (state && state.invoiceNote) || undefined
  );
};

export const getCostPlaceFactory = (key: CheckoutKey): MemoizedSelector<StateWithCheckout, string | undefined> => {
  return createSelector(
    getCheckoutReviewStepFactory(key),
    (state: CheckoutReviewStepState) => (state && state.costPlace) || undefined
  );
};

export const getApproverCommentFactory = (key: CheckoutKey): MemoizedSelector<StateWithCheckout, B2BComment | undefined> => {
  return createSelector(
    getCheckoutReviewStepFactory(key),
    (state: CheckoutReviewStepState) => (state && state.approverComment) || undefined
  );
};

export const getBuyerCommentFactory = (key: CheckoutKey): MemoizedSelector<StateWithCheckout, B2BComment | undefined> => {
  return createSelector(
    getCheckoutReviewStepFactory(key),
    (state: CheckoutReviewStepState) => (state && state.buyerComment) || undefined
  );
};
